import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Employee, EmployeeUserOption } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { useHrStructure } from './StructurePage';
import './hr.css';

const EMPTY = {
  personnelNumber: '', firstName: '', lastName: '', position: '', department: '', email: '', phone: '', street: '', postalCode: '', city: '',
  emergencyName: '', emergencyPhone: '', hiredOn: '', leftOn: '', userId: '', vacationDaysPerYear: '', weeklyHours: '',
};

/** „27,5“ → 27.5, leer → null, Unsinn → undefined (KON-120) */
const parseDays = (text: string): number | null | undefined => {
  const t = text.trim().replace(',', '.');
  if (!t) return null;
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : undefined;
};
type Fields = typeof EMPTY;

/**
 * Mitarbeiter anlegen oder bearbeiten. Die sensiblen Angaben (Geburtsdatum, Steuer- und Sozialversicherungsnummer, Bankdaten) pflegt man unter „Lohn und
 * Abrechnung“ am Mitarbeiter, dort sind sie durch ein eigenes Recht geschützt.
 */
export function EmployeeFormPage() {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can } = useSession();

  const existing = useQuery({ queryKey: ['employee', id], queryFn: async () => (await api<{ data: Employee }>('GET', `/employees/${id}`)).data, enabled: !isCreate });
  const structure = useHrStructure().data;
  const [values, setValues] = useState<Fields>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const loaded = useRef(false);

  const userOptions = useQuery({
    queryKey: ['employee-user-options', existing.data?.userId ?? ''],
    queryFn: async () => (await api<{ data: EmployeeUserOption[] }>('GET', `/employees/user-options${existing.data?.userId ? `?include=${existing.data.userId}` : ''}`)).data,
    enabled: isCreate || !!existing.data,
  });

  useEffect(() => {
    const e = existing.data;
    if (loaded.current || !e) return;
    loaded.current = true;
    setValues({
      personnelNumber: e.personnelNumber, firstName: e.firstName, lastName: e.lastName, position: e.position ?? '', department: e.department ?? '', email: e.email ?? '',
      phone: e.phone ?? '', street: e.street ?? '', postalCode: e.postalCode ?? '', city: e.city ?? '', emergencyName: e.emergencyName ?? '', emergencyPhone: e.emergencyPhone ?? '',
      hiredOn: e.hiredOn ?? '', leftOn: e.leftOn ?? '', userId: e.userId ?? '',
      vacationDaysPerYear: e.vacationDaysPerYear === null ? '' : String(e.vacationDaysPerYear).replace('.', ','),
      weeklyHours: e.weeklyHours === null ? '' : String(e.weeklyHours).replace('.', ','),
    });
  }, [existing.data]);

  const set = (key: keyof Fields) => (value: string) => setValues((v) => ({ ...v, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const { vacationDaysPerYear: vacationText, weeklyHours: hoursText, ...plain } = values;
      const vacation = parseDays(vacationText);
      const hours = parseDays(hoursText);
      if (hours === undefined) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { weeklyHours: 'Bitte eine Zahl angeben, zum Beispiel 40 oder 37,5.' });
      if (vacation === undefined) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { vacationDaysPerYear: 'Bitte eine Zahl angeben, zum Beispiel 30 oder 27,5.' });
      const trimmed = Object.fromEntries(Object.entries(plain).map(([k, v]) => [k, v.trim()]));
      if (isCreate) {
        // Beim Anlegen bleiben leere Felder weg, damit die Personalnummer automatisch vergeben wird
        const body = Object.fromEntries(Object.entries(trimmed).filter(([, v]) => v !== ''));
        return api<{ data: Employee }>('POST', '/employees', { ...body, ...(vacation !== null ? { vacationDaysPerYear: vacation } : {}), ...(hours !== null ? { weeklyHours: hours } : {}) });
      }
      // Beim Ändern löscht eine leere Eingabe den Wert
      const { personnelNumber, userId, ...rest } = trimmed;
      return api<{ data: Employee }>('PATCH', `/employees/${id}`, { ...rest, personnelNumber, userId: userId || null, vacationDaysPerYear: vacation, weeklyHours: hours });
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['employees'] });
      client.invalidateQueries({ queryKey: ['employee-user-options'] });
      client.removeQueries({ queryKey: ['employee', res.data.id] });
      client.removeQueries({ queryKey: ['employee-history', res.data.id] });
      navigate(`/mitarbeiter/${res.data.id}`, { replace: true });
    },
    onError: (e) => {
      const fieldErrors = e instanceof ApiError && e.status === 422;
      if (e instanceof ApiError) {
        setErrors(fieldErrors ? e.fields : {});
        setFormError(fieldErrors ? 'Bitte prüfen Sie Ihre Eingaben. Die Hinweise stehen an den markierten Feldern.' : e.message);
      } else {
        setFormError('Der Mitarbeiter konnte nicht gespeichert werden.');
      }
      // Zum ersten fehlerhaften Feld springen statt an den Seitenanfang, das Feld liegt sonst oft außerhalb des Bildschirms
      window.setTimeout(() => {
        const first = document.querySelector<HTMLElement>('[aria-invalid="true"]');
        if (first) {
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
          first.focus({ preventScroll: true });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    save.mutate();
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !existing.data)) {
    return (
      <div className="card narrow">
        <h1>Mitarbeiter nicht gefunden</h1>
        <Link to="/mitarbeiter">Zur Liste</Link>
      </div>
    );
  }

  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const text = (key: keyof Fields, label: string, extra: { required?: boolean; type?: string; max?: number; hint?: string } = {}) => (
    <label className="field">
      <span>{label}{extra.required && <> <abbr title="Pflichtfeld">*</abbr></>}</span>
      <input className="input" type={extra.type ?? 'text'} value={values[key]} onChange={(e) => set(key)(e.target.value)} maxLength={extra.max} autoComplete="off" aria-invalid={!!errors[key]} />
      {error(key)}
      {extra.hint && <small className="muted">{extra.hint}</small>}
    </label>
  );

  /**
   * Position und Abteilung: Sind sie unter „Abteilungen & Positionen“ angelegt, wählt man sie aus. Ein älterer Text, den es dort nicht gibt, bleibt als Eintrag stehen, damit er sich nicht
   * versehentlich ändert. Ohne Struktur bleibt es ein Textfeld wie bisher.
   */
  const pick = (key: 'position' | 'department', label: string) => {
    const names = (key === 'position' ? structure?.positions : structure?.departments)?.map((x) => x.name) ?? [];
    if (names.length === 0) return text(key, label, { max: 100 });
    const current = values[key];
    const legacy = current && !names.some((n) => n.toLowerCase() === current.toLowerCase());
    return (
      <label className="field">
        <span>{label}</span>
        <select
          className="input"
          value={current}
          onChange={(e) => {
            const chosen = e.target.value;
            set(key)(chosen);
            // Eine Position bringt ihre Abteilung mit, solange dort noch nichts steht
            if (key === 'position' && chosen && !values.department) {
              const dept = structure?.positions.find((p) => p.name === chosen)?.departmentName;
              if (dept) set('department')(dept);
            }
          }}
          aria-invalid={!!errors[key]}
        >
          <option value="">– keine Angabe –</option>
          {legacy && <option value={current}>{current} (nicht in der Struktur)</option>}
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        {error(key)}
        <small className="muted">Die Auswahl pflegen Sie unter <Link to="/einrichtung/abteilungen-positionen">Abteilungen &amp; Positionen</Link>.</small>
      </label>
    );
  };

  return (
    <>
      <p><Link to={isCreate ? '/mitarbeiter' : `/mitarbeiter/${id}`} className="back-link">← {isCreate ? 'Mitarbeiter' : 'Zur Karte'}</Link></p>
      <div className="page-head"><h1>{isCreate ? 'Neuer Mitarbeiter' : `${existing.data!.firstName} ${existing.data!.lastName} bearbeiten`}</h1></div>

      <form className="card" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}

        <h2 className="form-section">Stammdaten</h2>
        <div className="form-grid">
          {text('firstName', 'Vorname', { required: true, max: 80 })}
          {text('lastName', 'Nachname', { required: true, max: 80 })}
          {text('personnelNumber', 'Personalnummer', { max: 20, hint: isCreate ? 'Leer lassen, dann vergibt Wirkia die nächste Nummer.' : undefined })}
          {pick('position', 'Position')}
          {pick('department', 'Abteilung')}
          {text('hiredOn', 'Eintritt', { type: 'date' })}
          {text('leftOn', 'Austritt', { type: 'date' })}
          {text('vacationDaysPerYear', 'Urlaubsanspruch (Tage im Jahr)', { max: 6, hint: 'Halbe Tage mit Komma, zum Beispiel 27,5. Daraus rechnet Wirkia den Resturlaub.' })}
          {text('weeklyHours', 'Wochenstunden', { max: 5, hint: 'Vertragliche Stunden pro Woche, zum Beispiel 40 oder 37,5. Daraus erstellt die Schichtplanung den Plan. Leer: Vorgabe der Position.' })}
        </div>

        <h2 className="form-section">Kontakt und Anschrift</h2>
        <div className="form-grid">
          {text('email', 'E-Mail (privat)', { type: 'email', max: 200 })}
          {text('phone', 'Telefon', { max: 40 })}
          {text('street', 'Straße und Hausnummer', { max: 120 })}
          {text('postalCode', 'PLZ', { max: 10 })}
          {text('city', 'Ort', { max: 80 })}
          {text('emergencyName', 'Notfallkontakt', { max: 120 })}
          {text('emergencyPhone', 'Telefon des Notfallkontakts', { max: 40 })}
        </div>

        <h2 className="form-section">Zugang zu Wirkia</h2>
        <label className="field">
          <span>Benutzerzugang</span>
          <select className="input" value={values.userId} onChange={(e) => set('userId')(e.target.value)} aria-invalid={!!errors.userId}>
            <option value="">Kein Zugang verknüpft</option>
            {(userOptions.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
          {error('userId')}
          <small className="muted">Nicht jeder Mitarbeiter braucht einen Zugang. Verknüpfen lassen sich nur Benutzer, die noch keinem Mitarbeiter zugeordnet sind. Benutzer legt der Inhaber in der Einrichtung an.</small>
        </label>

        <p className="muted small">Steuer-ID, Sozialversicherungsnummer, Bankverbindung, Geburtsdatum, Vergütung und die übrigen Lohndaten pflegen Sie am Mitarbeiter unter „Lohn und Abrechnung“.</p>

        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : isCreate ? 'Mitarbeiter anlegen' : 'Speichern'}</button>
          <Link className="btn btn--secondary" to={isCreate ? '/mitarbeiter' : `/mitarbeiter/${id}`}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
